import { getStoredTenantId } from "./lib/storage.js";

export const API_BASE = "/api/v1";

export class ApiError extends Error {
  constructor(status, code, message, body = null) {
    super(message || code);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

/**
 * Turn a non-2xx Response into an ApiError. The contract's envelope is
 * `{ "error": { "code", "message" } }`; anything else (HTML from a proxy,
 * empty body) gets a synthetic `http_<status>` code.
 */
export async function parseErrorResponse(res) {
  let body = null;
  let text = "";
  try {
    text = await res.text();
    body = text ? JSON.parse(text) : null;
  } catch {
    body = null;
  }
  const env = body && typeof body === "object" ? body.error : null;
  if (env && typeof env === "object" && typeof env.code === "string") {
    return new ApiError(res.status, env.code, env.message || env.code, body);
  }
  return new ApiError(res.status, `http_${res.status}`, res.statusText || `HTTP ${res.status}`, body ?? text);
}

/**
 * Create an API client. Dependencies are injectable for tests.
 * - getTenantId(): current tenant id or null (sent as X-Tenant-Id)
 * - getToken(): async bearer token or null (OIDC mode)
 * - onUnauthorized(err): called on any 401
 */
export function createApi({
  base = API_BASE,
  fetchImpl = (...args) => globalThis.fetch(...args),
  getTenantId = getStoredTenantId,
  getToken = async () => null,
  onUnauthorized = () => {},
} = {}) {
  async function request(method, path, { body, query, tenant = true, signal } = {}) {
    let url = base + path;
    if (query) {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(query)) if (v !== undefined && v !== null) qs.set(k, String(v));
      const s = qs.toString();
      if (s) url += (url.includes("?") ? "&" : "?") + s;
    }
    const headers = { Accept: "application/json" };
    if (body !== undefined) headers["Content-Type"] = "application/json";
    if (tenant) {
      const tid = getTenantId();
      if (tid) headers["X-Tenant-Id"] = tid;
    }
    const token = await getToken();
    if (token) headers.Authorization = `Bearer ${token}`;

    let res;
    try {
      res = await fetchImpl(url, {
        method,
        headers,
        credentials: "include",
        body: body !== undefined ? JSON.stringify(body) : undefined,
        signal,
      });
    } catch (e) {
      if (e && e.name === "AbortError") throw e;
      throw new ApiError(0, "network_error", e?.message || "Network error");
    }

    if (!res.ok) {
      const err = await parseErrorResponse(res);
      if (res.status === 401) onUnauthorized(err);
      throw err;
    }
    if (res.status === 204) return null;
    const text = await res.text();
    if (!text) return null;
    try {
      return JSON.parse(text);
    } catch {
      throw new ApiError(res.status, "invalid_response", "Invalid JSON response");
    }
  }

  return {
    request,
    get: (path, opts) => request("GET", path, opts),
    post: (path, body, opts) => request("POST", path, { ...opts, body: body ?? {} }),
    patch: (path, body, opts) => request("PATCH", path, { ...opts, body: body ?? {} }),
    del: (path, opts) => request("DELETE", path, opts),
  };
}

// --- app singleton -------------------------------------------------------

let tokenProvider = async () => null;
let unauthorizedHandler = () => {};

export function setTokenProvider(fn) {
  tokenProvider = fn || (async () => null);
}
export function setUnauthorizedHandler(fn) {
  unauthorizedHandler = fn || (() => {});
}

export const api = createApi({
  getToken: () => tokenProvider(),
  onUnauthorized: (err) => unauthorizedHandler(err),
});

/** Endpoints not scoped to a tenant: don't send X-Tenant-Id. */
const NO_TENANT = { tenant: false };

export const endpoints = {
  authConfig: () => api.get("/auth/config", NO_TENANT),
  devLogin: (email) => api.post("/auth/dev-login", { email }, NO_TENANT),
  logout: () => api.post("/auth/logout", {}, NO_TENANT),
  me: () => api.get("/me", NO_TENANT),
  patchMe: (body) => api.patch("/me", body, NO_TENANT),
  createTenant: (body) => api.post("/tenants", body, NO_TENANT),
  tenant: () => api.get("/tenant"),
  patchTenant: (body) => api.patch("/tenant", body),
  acceptTerms: (body) => api.post("/tenant/terms", body),
  legalVersions: () => api.get("/legal/versions", NO_TENANT),
  members: () => api.get("/tenant/members"),
  inviteMember: (body) => api.post("/tenant/members", body),
  removeMember: (userId) => api.del(`/tenant/members/${encodeURIComponent(userId)}`),
  plans: () => api.get("/billing/plans", NO_TENANT),
  checkout: (plan) => api.post("/billing/checkout", { plan }),
  billingPortal: () => api.post("/billing/portal", {}),
  credits: () => api.get("/billing/credits"),
  verification: () => api.get("/verification"),
  targetTypes: () => api.get("/target-types"),
  investigations: () => api.get("/investigations"),
  investigation: (id) => api.get(`/investigations/${encodeURIComponent(id)}`),
  createInvestigation: (body) => api.post("/investigations", body),
  estimate: (targetType) => api.post("/investigations/estimate", { targetType }),
  startJob: (investigationId) => api.post(`/investigations/${encodeURIComponent(investigationId)}/jobs`, {}),
  job: (id) => api.get(`/jobs/${encodeURIComponent(id)}`),
  cancelJob: (id) => api.post(`/jobs/${encodeURIComponent(id)}/cancel`, {}),
  findings: (jobId) => api.get(`/jobs/${encodeURIComponent(jobId)}/findings`),
  patchFinding: (id, body) => api.patch(`/findings/${encodeURIComponent(id)}`, body),
  graph: (jobId) => api.get(`/jobs/${encodeURIComponent(jobId)}/graph`),
  evidence: (id) => api.get(`/evidence/${encodeURIComponent(id)}`),
  evidenceUrl: (id) => api.get(`/evidence/${encodeURIComponent(id)}/url`),
  createReport: (body) => api.post("/reports", body),
  reports: (investigationId) => api.get("/reports", { query: { investigationId } }),
  reportUrl: (id) => api.get(`/reports/${encodeURIComponent(id)}/url`),
};

// EventSource can't send X-Tenant-Id, so the API also accepts ?tenantId= on GET requests.
export const jobEventsUrl = (jobId) => {
  const tid = getStoredTenantId();
  return `${API_BASE}/jobs/${encodeURIComponent(jobId)}/events${tid ? `?tenantId=${encodeURIComponent(tid)}` : ""}`;
};

export const TERMINAL_JOB_STATUSES = ["succeeded", "partial", "failed", "cancelled", "denied"];
export const isTerminal = (status) => TERMINAL_JOB_STATUSES.includes(status);
